// ======================================================
// BANSURI SCALE ENGINE (Correct Physical Model)
// ======================================================

export type Difficulty =
    | "Very Easy"
    | "Easy"
    | "Medium"
    | "Hard"
    | "Very Hard";

export type HoleState = "x" | "o" | "|";
export type FingeringPattern = HoleState[]; // 6 holes
export type ScaleType = "Major" | "Natural Minor";
export type ScalePlan = {
    fluteBase: string;
    targetScale: string;
    westernNotes: string[];
    swaras: string[];
    fingerings: FingeringPattern[];
    difficulty: Difficulty;
    newSa: string;
    halfHoleCount: number;
    komalCount: number;
    alteredCount: number;
};

export function baseSwaraName(swara: string): string {
    return swara.endsWith("'") ? swara.slice(0, -1) : swara;
}


// ======================================================
// 1️⃣ PHYSICAL FINGERING (Fixed Reality)
// ======================================================
// IMPORTANT: in north indian 6 Hole flute  index 0 represents: first hole near blowing hole
export const BASE_FINGERING: Record<string, FingeringPattern> = {
    "Sa": ["x", "x", "x", "o", "o", "o"], // means top 3 holes near blowing holes are closed and rest bottom three opened.
    "Sa'": ["x", "x", "x", "o", "o", "o"], // Upper octave (Taar Sa) - same holes, overblown
    "komal Re": ["x", "x", "|", "o", "o", "o"],
    "Re": ["x", "x", "o", "o", "o", "o"],
    "komal Ga": ["x", "|", "o", "o", "o", "o"],
    "Ga": ["x", "o", "o", "o", "o", "o"],
    "Ma (Shuddha)": ["|", "o", "o", "o", "o", "o"],
    "Ma (Tivra)": ["o", "o", "o", "o", "o", "o"],
    "Pa": ["x", "x", "x", "x", "x", "x"],
    "komal Dha": ["x", "x", "x", "x", "x", "|"],
    "Dha": ["x", "x", "x", "x", "x", "o"],
    "komal Ni": ["x", "x", "x", "x", "|", "o"],
    "Ni": ["x", "x", "x", "x", "o", "o"],
};


// ======================================================
// 2️⃣ PITCH ENGINE (Pure Western Math)
// ======================================================

const NOTE_TO_PC: Record<string, number> = {
    C: 0, "C#": 1, D: 2, "D#": 3, E: 4,
    F: 5, "F#": 6, G: 7, "G#": 8,
    A: 9, "A#": 10, B: 11,
};

const PC_TO_NOTE = [
    "C", "C#", "D", "D#", "E", "F",
    "F#", "G", "G#", "A", "A#", "B"
];

const MAJOR_INTERVALS = [0, 2, 4, 5, 7, 9, 11];
const NATURAL_MINOR_INTERVALS = [0, 2, 3, 5, 7, 8, 10];
const DIFFICULTY_RANK: Record<Difficulty, number> = {
    "Very Easy": 1,
    "Easy": 2,
    "Medium": 3,
    "Hard": 4,
    "Very Hard": 5
};

function mod(n: number) {
    return (n + 12) % 12;
}

export function buildWesternMajorScale(root: string): number[] {
    const base = NOTE_TO_PC[root];
    if (base === undefined) {
        throw new Error(`Invalid note: ${root}`);
    }
    return MAJOR_INTERVALS.map(i => mod(base + i));
}

export function buildWesternNaturalMinorScale(root: string): number[] {
    const base = NOTE_TO_PC[root];
    if (base === undefined) {
        throw new Error(`Invalid note: ${root}`);
    }
    return NATURAL_MINOR_INTERVALS.map(i => mod(base + i));
}


// ======================================================
// 3️⃣ SWARA RESOLVER (Relative to Flute Base)
// ======================================================

const SWARA_FROM_DIFF: Record<number, string> = {
    0: "Sa",
    1: "komal Re",
    2: "Re",
    3: "komal Ga",
    4: "Ga",
    5: "Ma (Shuddha)",
    6: "Ma (Tivra)",
    7: "Pa",
    8: "komal Dha",
    9: "Dha",
    10: "komal Ni",
    11: "Ni"
};

const SWARA_TO_DIFF: Record<string, number> = Object.fromEntries(
    Object.entries(SWARA_FROM_DIFF).map(([diff, swara]) => [swara, Number(diff)])
) as Record<string, number>;

export function swaraToDiff(swara: string): number {
    const normalized = baseSwaraName(swara);
    const diff = SWARA_TO_DIFF[normalized];
    if (diff === undefined) {
        throw new Error(`Invalid swara: ${swara}`);
    }
    return diff;
}

export function diffToSwara(diff: number): string {
    return SWARA_FROM_DIFF[mod(diff)]!;
}

export function relativeSwaraForNewSa(
    absoluteSwara: string,
    newSaSwara: string
): string {
    const absoluteDiff = swaraToDiff(absoluteSwara);
    const newSaDiff = swaraToDiff(newSaSwara);
    return diffToSwara(absoluteDiff - newSaDiff);
}

export function pitchToSwara(
    pitch: number,
    fluteBase: string
): string {
    const basePc = NOTE_TO_PC[fluteBase];
    if (basePc === undefined) {
        throw new Error(`Invalid flute base: ${fluteBase}`);
    }

    const diff = mod(pitch - basePc);
    return SWARA_FROM_DIFF[diff]!;
}


function countHalfHoles(
    fingerings: FingeringPattern[]
): number {
    return fingerings.reduce((acc, f) =>
        acc + f.filter(h => h === "|").length
        , 0);
}

function computeDifficulty(
    komalCount: number,
    halfHoleCount: number,
    alteredCount: number
): Difficulty {
    // Prioritize komal swaras for playability; they are the biggest effort driver.
    const effortScore = (komalCount * 2) + halfHoleCount + Math.max(0, alteredCount - komalCount);
    if (effortScore === 0) return "Very Easy";
    if (effortScore <= 2) return "Easy";
    if (effortScore <= 4) return "Medium";
    if (effortScore <= 6) return "Hard";
    return "Very Hard";
}

function buildBansuriPlanForScale(
    fluteBase: string,
    targetKey: string,
    intervals: number[],
    scaleType: ScaleType
): ScalePlan {
    const flutePc = NOTE_TO_PC[fluteBase];
    if (flutePc === undefined) {
        throw new Error(`Invalid flute base: ${fluteBase}`);
    }
    const targetPc = NOTE_TO_PC[targetKey];
    if (targetPc === undefined) {
        throw new Error(`Invalid target key: ${targetKey}`);
    }

    const targetScale = intervals.map(i => mod(targetPc + i));
    const coreSwaras = targetScale.map(p => pitchToSwara(p, fluteBase));
    const swaras = [...coreSwaras, `${coreSwaras[0]}'`];
    const westernNotes = targetScale.map(p => PC_TO_NOTE[p]!);
    const octaveRoot = `${westernNotes[0]}'`;
    const normalizedFingerings = swaras.map(s => BASE_FINGERING[baseSwaraName(s)]!);
    const halfHoleCount = countHalfHoles(normalizedFingerings);
    const komalCount = coreSwaras.filter(s => s.startsWith("komal ")).length;
    const fluteNaturalMajor = new Set(MAJOR_INTERVALS.map(i => mod(flutePc + i)));
    const alteredCount = targetScale.reduce((acc, pitch) =>
        acc + (fluteNaturalMajor.has(pitch) ? 0 : 1)
        , 0);

    return {
        fluteBase,
        targetScale: `${targetKey} ${scaleType}`,
        westernNotes: [...westernNotes, octaveRoot],
        swaras,
        fingerings: normalizedFingerings,
        difficulty: computeDifficulty(komalCount, halfHoleCount, alteredCount),
        newSa: coreSwaras[0]!,
        halfHoleCount,
        komalCount,
        alteredCount
    };
}


// ======================================================
// 5️⃣ MAIN SCALE BUILDER (Correct Model)
// ======================================================

export function buildBansuriMajorPlan(
    fluteBase: string,
    targetKey: string
) : ScalePlan {
    return buildBansuriPlanForScale(fluteBase, targetKey, MAJOR_INTERVALS, "Major");
}

export function buildBansuriMinorPlan(
    fluteBase: string,
    targetKey: string
): ScalePlan {
    return buildBansuriPlanForScale(fluteBase, targetKey, NATURAL_MINOR_INTERVALS, "Natural Minor");
}


// ======================================================
// 6️⃣ Convenience Function (Your Old API Equivalent)
// ======================================================

export function getMajorScalePlan(
    fluteBase: string,
    target: string
) : ScalePlan {
    return buildBansuriMajorPlan(fluteBase, target);
}

export function getMinorScalePlan(
    fluteBase: string,
    target: string
): ScalePlan {
    return buildBansuriMinorPlan(fluteBase, target);
}

export function rankFlutesForMinorKey(targetKey: string): ScalePlan[] {
    return PC_TO_NOTE
        .map(fluteBase => buildBansuriMinorPlan(fluteBase, targetKey))
        .sort((a, b) =>
            a.komalCount - b.komalCount
            || a.halfHoleCount - b.halfHoleCount
            || a.alteredCount - b.alteredCount
            || DIFFICULTY_RANK[a.difficulty] - DIFFICULTY_RANK[b.difficulty]
            || PC_TO_NOTE.indexOf(a.fluteBase) - PC_TO_NOTE.indexOf(b.fluteBase)
        );
}

export function rankFlutesForMajorKey(targetKey: string): ScalePlan[] {
    return PC_TO_NOTE
        .map(fluteBase => buildBansuriMajorPlan(fluteBase, targetKey))
        .sort((a, b) =>
            a.komalCount - b.komalCount
            || a.halfHoleCount - b.halfHoleCount
            || a.alteredCount - b.alteredCount
            || DIFFICULTY_RANK[a.difficulty] - DIFFICULTY_RANK[b.difficulty]
            || PC_TO_NOTE.indexOf(a.fluteBase) - PC_TO_NOTE.indexOf(b.fluteBase)
        );
}


// ======================================================
// 7️⃣ Reverse Lookup (Fingering → Swara)
// ======================================================

export function getSwaraFromFingering(
    fingering: FingeringPattern
): string | null {
    for (const [swara, pattern] of Object.entries(BASE_FINGERING)) {
        if (pattern.join("") === fingering.join("")) {
            return swara;
        }
    }
    return null;
}
